import { SAJU_METRICS, SUBJECT_KEYS, componentError as error, requestSaju, sameSubject, validateCalculation, validateSubject } from './component-saju.mjs';
import { PALJA_COMPONENT } from './component-saju-palja.mjs';
import { RELATIONS_COMPONENT } from './component-saju-relations.mjs';
import { CENTURY_COMPONENT, SEUN_COMPONENT, WOLUN_COMPONENT } from './component-saju-periods.mjs';
import { NARRATIVE_COMPONENT } from './component-saju-narrative.mjs';

const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const exactKeys = (value, keys) => object(value) && Object.keys(value).length === keys.length && keys.every(key => Object.hasOwn(value, key));
const identifier = value => typeof value === 'string' && /^[A-Za-z0-9_-]{1,100}$/.test(value) && !['__proto__', 'prototype', 'constructor'].includes(value);
export const DAEUN_METRICS = SAJU_METRICS;
const DAEUN_SOURCE = 'sajug:/api/daeun_list';

export function validateDaeunProps(props) {
  if (!object(props) || Object.keys(props).some(key => ![...SUBJECT_KEYS, 'title', 'metric', 'accent'].includes(key))) throw error('INVALID_COMPONENT', '대운 설정 필드를 확인해 주세요.');
  validateSubject(props);
  if (typeof props.title !== 'string' || !props.title.trim() || props.title.length > 100 || !Object.hasOwn(DAEUN_METRICS, props.metric) || !/^#[0-9a-f]{6}$/i.test(props.accent)) throw error('INVALID_COMPONENT', '제목, 운세 항목, 강조색을 확인해 주세요.');
  return props;
}

/** Keep only the authoritative, documented API fields needed by this component. */
export function normalizeDaeunResponse(payload) {
  if (!object(payload) || !Array.isArray(payload.items) || payload.items.length !== 10 || payload.total_count !== 10) throw error('INVALID_COMPONENT_DATA', '평생 대운 10개 구간을 모두 받지 못했습니다. 기존 결과는 유지됩니다.');
  const seen = new Set();
  const items = payload.items.map(item => {
    if (!object(item) || !Number.isInteger(item.index) || seen.has(item.index)
      || typeof item.ganji !== 'string' || !item.ganji.length || item.ganji.length > 20
      || !Number.isFinite(item.start_age) || !Number.isFinite(item.end_age) || item.start_age < 0 || item.end_age < item.start_age || item.end_age > 200 || !object(item.scores)) throw error('INVALID_COMPONENT_DATA', '대운 계산 결과의 구간 또는 점수 형식이 올바르지 않습니다.');
    seen.add(item.index);
    const scores = {};
    for (const metric of Object.keys(DAEUN_METRICS)) {
      const score = item.scores[metric];
      if (score === undefined || score === null) scores[metric] = null;
      else if (typeof score === 'number' && Number.isFinite(score) && score >= 0 && score <= 100) scores[metric] = score;
      else throw error('INVALID_COMPONENT_DATA', '대운 점수는 0~100 범위여야 합니다.');
    }
    return { index: item.index, ganji: item.ganji, startAge: item.start_age, endAge: item.end_age, scores };
  });
  if (!seen.has(payload.selected_index) || items.some((item, index) => index > 0 && (item.index <= items[index - 1].index || item.startAge <= items[index - 1].endAge))) throw error('INVALID_COMPONENT_DATA', '대운 구간 순서와 현재 구간을 확인하지 못했습니다.');
  return { items, selectedIndex: payload.selected_index };
}

export async function calculateDaeun(props, options = {}) {
  validateDaeunProps(props);
  const data = normalizeDaeunResponse(await requestSaju('daeun', props, options));
  return { source: DAEUN_SOURCE, calculatedAt: new Date().toISOString(), data };
}

export function renderDaeunScene(props, snapshot) {
  validateDaeunProps(props);
  const elements = [];
  const rect = (key, x, y, width, height, fill, extra = {}) => elements.push({ key, type: 'rect', x, y, width, height, fill, ...extra });
  const text = (key, x, y, width, height, value, fontSize, color = '#24352f') => elements.push({ key, type: 'text', x, y, width, height, text: value, fontSize, fontFamily: 'Content KoPub Dotum', color });
  rect('background', 0, 0, 1000, 640, '#f6f8f6');
  text('title', 32, 26, 936, 44, props.title, 30);
  text('subtitle', 32, 78, 936, 24, `${DAEUN_METRICS[props.metric]} · 10년 단위 흐름`, 15, '#52665d');
  snapshot.data.items.forEach((item, slot) => {
    const x = 32 + (slot % 5) * 190, y = 128 + Math.floor(slot / 5) * 236;
    rect(`period-${slot}`, x, y, 176, 218, '#ffffff');
    text(`age-${slot}`, x + 12, y + 14, 152, 22, `${item.startAge}~${item.endAge}세`, 14, '#52665d');
    text(`ganji-${slot}`, x + 12, y + 43, 152, 38, item.ganji, 28, props.accent);
    const score = item.scores[props.metric];
    rect(`track-${slot}`, x + 12, y + 100, 152, 12, '#e9eeeb');
    rect(`score-bar-${slot}`, x + 12, y + 100, Math.max(1, score === null ? 1 : 152 * score / 100), 12, props.accent, { dataProps: ['width'] });
    text(`score-${slot}`, x + 12, y + 128, 152, 30, score === null ? '점수 미제공' : `${Math.round(score * 10) / 10}점`, 22);
    text(`current-${slot}`, x + 12, y + 180, 152, 20, item.index === snapshot.data.selectedIndex ? '현재 대운' : `${slot + 1}번째 대운`, 13, '#52665d');
  });
  text('calculated-at', 32, 606, 936, 20, `계산: ${snapshot.calculatedAt.slice(0, 10)} · 사주 대운 계산 결과`, 12, '#52665d');
  return { width: 1000, height: 640, elements };
}

function validateDaeunData(data) {
  if (!exactKeys(data, ['items', 'selectedIndex']) || !Array.isArray(data.items) || data.items.some(item =>
    !exactKeys(item, ['index', 'ganji', 'startAge', 'endAge', 'scores']) || !exactKeys(item.scores, Object.keys(DAEUN_METRICS)))) {
    throw error('INVALID_COMPONENT', '컴포넌트 결과의 필드가 누락되었거나 알 수 없는 필드가 있습니다.');
  }
  normalizeDaeunResponse({ total_count: 10, selected_index: data.selectedIndex, items: data.items.map(item => ({ index: item.index, ganji: item.ganji, start_age: item.startAge, end_age: item.endAge, scores: item.scores })) });
}

const daeun = Object.freeze({ type: 'saju.daeun', version: 1, label: '평생 대운',
  description: '생년월일로 계산한 10개 대운 구간을 현재 페이지에 추가합니다.',
  fields: Object.freeze([
    Object.freeze({ name: 'title', label: '제목', kind: 'text', default: '평생 대운' }),
    Object.freeze({ name: 'metric', label: '표시할 운세', kind: 'select', options: DAEUN_METRICS, default: 'balance' }),
    Object.freeze({ name: 'accent', label: '강조색', kind: 'color', default: '#28745b' }),
  ]),
  validateProps: validateDaeunProps, validateSnapshot: snapshot => validateCalculation(snapshot, DAEUN_SOURCE, validateDaeunData),
  calculate: calculateDaeun, render: renderDaeunScene, calculationChanged: (before, after) => !sameSubject(before, after) });
/**
 * Every data component a page can hold; each is calculated by the Saju app for one person. One a page gets only from an
 * import (the book's narrative boxes: `insertable: false`) is not offered to add by hand.
 */
export const CONTENT_COMPONENTS = Object.freeze([daeun, PALJA_COMPONENT, RELATIONS_COMPONENT, SEUN_COMPONENT, WOLUN_COMPONENT, CENTURY_COMPONENT, NARRATIVE_COMPONENT]);
export function getContentComponent(type) {
  const plugin = CONTENT_COMPONENTS.find(item => item.type === type);
  if (!plugin) throw error('UNKNOWN_COMPONENT', '등록되지 않은 컴포넌트입니다.');
  return plugin;
}

/**
 * Rules across a document's pages: a plugin whose components must agree with each other (validateDocument, given every
 * component of its type in page order) names the ones that do not. Run on components that passed their own checks.
 */
export function validateDocumentComponents(sections, issue) {
  const byType = new Map();
  sections.forEach((section, index) => (Array.isArray(section?.components) ? section.components : []).forEach((component, at) => {
    const plugin = CONTENT_COMPONENTS.find(item => item.type === component?.type);
    if (!plugin?.validateDocument) return;
    if (!byType.has(plugin)) byType.set(plugin, []);
    byType.get(plugin).push({ component, path: `sections[${index}].components[${at}]` });
  }));
  for (const [plugin, entries] of byType) {
    for (const { index, message } of plugin.validateDocument(entries.map(entry => entry.component))) issue(entries[index].path, message);
  }
}

/** Native bindings are bounded JSON, never executable content or remote resources. */
export function validateSectionComponents(components, path, issue) {
  if (!Array.isArray(components) || components.length > 20) { issue(path, '페이지당 컴포넌트는 20개 이하이어야 합니다.'); return; }
  const ids = new Set();
  for (const [index, component] of components.entries()) {
    const at = `${path}[${index}]`;
    try {
      if (!object(component) || Object.keys(component).some(key => !['id', 'type', 'version', 'props', 'snapshot', 'binding'].includes(key)) || !identifier(component.id) || ids.has(component.id)) throw error('INVALID_COMPONENT', '컴포넌트 ID나 저장 형식이 올바르지 않습니다.');
      ids.add(component.id);
      const plugin = getContentComponent(component.type);
      if (component.version !== plugin.version) throw error('INVALID_COMPONENT', '지원하지 않는 컴포넌트 버전입니다.');
      plugin.validateProps(component.props);
      plugin.validateSnapshot(component.snapshot);
      if (!object(component.binding)) throw error('INVALID_COMPONENT', '편집기 연결 정보가 필요합니다.');
      let nodes = 0;
      const walk = (value, trail = []) => {
        const depth = trail.length;
        if (++nodes > 12000 || depth > 12) throw error('INVALID_COMPONENT', '컴포넌트 연결 정보 한도를 넘었습니다.');
        if (value === null || typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value))) return;
        // BlockSuite keeps comparison text here; its consumers use textContent
        // and Y.Text, never HTML, CSS or a resource URL.
        const baselineText = depth === 4 && trail[0] === 'elements' && trail[2] === 'baseline' && trail[3] === 'text'
          && component.binding.version === 1 && ['page', 'edgeless'].includes(component.binding.mode)
          && component.binding.elements?.[trail[1]]?.type === 'text' && component.binding.elements[trail[1]].baseline?.type === 'text';
        if (typeof value === 'string' && value.length <= 2000 && (baselineText || !/(?:javascript:|data:|https?:|url\s*\()/i.test(value))) return;
        if (Array.isArray(value)) { value.forEach((child, index) => walk(child, [...trail, index])); return; }
        if (!object(value) || ![Object.prototype, null].includes(Object.getPrototypeOf(value))) throw error('INVALID_COMPONENT', '컴포넌트 연결 정보는 JSON이어야 합니다.');
        for (const [key, child] of Object.entries(value)) {
          if (/^(?:__proto__|prototype|constructor|innerHTML|outerHTML|script|on[a-z]+)$/i.test(key)) throw error('INVALID_COMPONENT', '실행 가능한 컴포넌트 속성은 저장할 수 없습니다.');
          walk(child, [...trail, key]);
        }
      };
      walk(component.binding);
    } catch (reason) { issue(at, reason.message); }
  }
}
